import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
    Button,
    IconButton,
    Chip,
    Box,
    Switch,
    FormControlLabel,
    Tooltip,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
} from "@mui/material";
import { Add, InfoOutlined, ModeEditOutlineTwoTone, CheckCircle, Cancel, ReplayOutlined, Download, RemoveCircleOutline } from "@mui/icons-material";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import {
    useLazyListVehicleHiringEntriesQuery,
    useReviewVehicleHiringEntryMutation,
    useWithdrawVehicleHiringEntryMutation,
} from "store/services/vehicleHiringEntryApi";
import TableFilter from "components/Shared/AppTable/TableFilter";
import AppTable from "components/Shared/AppTable";
import CustomTabs from "components/Shared/CustomTabs";
import HighlightedText from "components/Shared/HighlightedText";
import { useAppMeta, useTablePagination, useAuth } from "hooks";
import { toast } from "react-toastify";
import VehicleHiringEntryResubmitDialog from "./VehicleHiringEntryComponents/VehicleHiringEntryResubmitDialog";
import { useAddNoteSourceMutation } from "store/services/noteApi";
import { exportVehicleHiringEntriesExcel } from "./VehicleHiringEntryComponents/vehicleHiringEntryExcelExport";

const HiringDatesCell = ({ dates }) => {
    if (!dates || !dates.length) return "N/A";
    const sorted = [...dates].sort((a, b) => new Date(a) - new Date(b));
    const formatted = sorted.map((d) =>
        new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric" })
    );
    const visible = formatted.slice(0, 2).join(", ");
    const rest = formatted.slice(2);
    if (!rest.length) return visible;
    return (
        <span>
            {visible},{" "}
            <Tooltip title={rest.join(", ")} arrow>
                <span style={{ cursor: "default", textDecoration: "underline dotted" }}>...</span>
            </Tooltip>
        </span>
    );
};


const VehicleHiringEntryListScreen = () => {
    const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
        useTablePagination();
    const [searchParams] = useSearchParams();
    const activeTab = searchParams.get("tab") || "Pending";
    const keyword = searchParams.get("search") || "";
    const breadcrumbs = [{ title: "Vehicle Hiring Entries" }];
    const { loggedInUser } = useAuth();

    const tabs = [
        { label: "Pending", value: "Pending" },
        { label: "Approved", value: "Approved" },
        { label: "Rejected", value: "Rejected" },
        { label: "Inactive", value: "Inactive" },
    ];

    const [reviewEntry, { isLoading: isReviewing }] =
        useReviewVehicleHiringEntryMutation();
    const [withdrawEntry, { isLoading: isWithdrawing }] = useWithdrawVehicleHiringEntryMutation();
    const [addNoteSource] = useAddNoteSourceMutation();
    const [resubmitTarget, setResubmitTarget] = useState(null);
    const [assignedToMe, setAssignedToMe] = useState(false);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [isExporting, setIsExporting] = useState(false);
    const [rejectTarget, setRejectTarget] = useState(null);
    const [rejectNoteText, setRejectNoteText] = useState("");
    const [rejectNoteError, setRejectNoteError] = useState("");
    const [isRejecting, setIsRejecting] = useState(false);

    const [withdrawTarget, setWithdrawTarget] = useState(null);
    const [withdrawNoteText, setWithdrawNoteText] = useState("");
    const [withdrawNoteError, setWithdrawNoteError] = useState("");
    const [isWithdrawingNote, setIsWithdrawingNote] = useState(false);

    const handleApprove = async (id) => {
        try {
            await reviewEntry({ id, approvalStatus: "Approved" }).unwrap();
            toast.success("Entry Approved successfully!");
        } catch (err) {
            toast.error(err?.data?.message ?? "Failed to update status");
        }
    };

    const openWithdrawDialog = (row) => {
        setWithdrawNoteText("");
        setWithdrawNoteError("");
        setWithdrawTarget(row);
    };

    const handleSubmitWithdraw = async () => {
        if (withdrawNoteText.trim().length < 3) {
            setWithdrawNoteError("Note must be at least 3 characters.");
            return;
        }
        setIsWithdrawingNote(true);
        try {
            await addNoteSource({
                content: `Withdraw Reason: ${withdrawNoteText.trim()}`,
                type: "Note",
                source: "VehicleHiringEntry",
                sourceRef: withdrawTarget._id,
            }).unwrap();
            await withdrawEntry(withdrawTarget._id).unwrap();
            toast.success("Entry Withdrawn successfully!");
            setWithdrawTarget(null);
        } catch (err) {
            toast.error(err?.data?.message ?? "Failed to withdraw entry");
        } finally {
            setIsWithdrawingNote(false);
        }
    };

    const openRejectDialog = (row) => {
        setRejectNoteText("");
        setRejectNoteError("");
        setRejectTarget(row);
    };

    const handleSubmitReject = async () => {
        if (rejectNoteText.trim().length < 3) {
            setRejectNoteError("Note must be at least 3 characters.");
            return;
        }
        setIsRejecting(true);
        try {
            await addNoteSource({
                content: `Rejection Reason: ${rejectNoteText.trim()}`,
                type: "Note",
                source: "VehicleHiringEntry",
                sourceRef: rejectTarget._id,
            }).unwrap();
            await reviewEntry({
                id: rejectTarget._id,
                approvalStatus: "Rejected",
            }).unwrap();
            toast.success("Entry Rejected successfully!");
            setRejectTarget(null);
        } catch (err) {
            toast.error(err?.data?.message ?? "Failed to update status");
        } finally {
            setIsRejecting(false);
        }
    };

    const columns = [
        {
            name: "Vehicle Reg. No",
            selector: (row) => row?.vehicleRegistrationNumber?.registration_number,
            cell: (row) => (
                <Link
                    title={row?.vehicleRegistrationNumber?.registration_number}
                    to={`/app/crm/vehicle-hiring-entry/${row?._id}/detail`}
                    className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
                >
                    {row?.vehicleRegistrationNumber?.registration_number ? (
                        <HighlightedText
                            text={row?.vehicleRegistrationNumber?.registration_number}
                            highlight={keyword}
                        />
                    ) : (
                        "N/A"
                    )}
                </Link>
            ),
        },
        {
            name: "Origin",
            selector: (row) => row.sourceBranch?.name,
            cell: (row) => row?.sourceBranch?.name ?? "N/A",
        },
        {
            name: "Destination",
            selector: (row) => row.destinationBranch?.name,
            cell: (row) => row?.destinationBranch?.name ?? "N/A",
        },
        {
            name: "Driver",
            selector: (row) => row.driverName,
            cell: (row) => (
                <div>
                    <span className="block text-sm">{row?.driverName || "N/A"}</span>
                    <span className="block text-xs text-gray-500">{row?.driverNumber || ""}</span>
                </div>
            ),
        },
        {
            name: "Approve / Submitted To",
            selector: (row) => row.responsibleUser?.name,
            cell: (row) => row?.responsibleUser?.name ?? "N/A",
        },
        // {
        //     name: "Hiring Fare",
        //     selector: (row) => row.hiringFare,
        //     cell: (row) => row?.hiringFare ?? "N/A",
        // },
        {
            name: "Total Fare",
            selector: (row) => row.totalFare,
            cell: (row) => row?.totalFare ?? "N/A",
        },
        {
            name: "Hiring Dates",
            cell: (row) => <HiringDatesCell dates={row?.hiringDates} />,
        },
        {
            name: "Vehicle Type",
            selector: (row) => row.vehicleType,
            cell: (row) => row?.vehicleType ?? "N/A",
        },
        {
            name: "Status",
            selector: (row) => row.approvalStatus,
            cell: (row) => (
                <Chip
                    label={row?.approvalStatus}
                    size="small"
                    color={
                        row?.approvalStatus === "Approved"
                            ? "success"
                            : row?.approvalStatus === "Rejected"
                                ? "error"
                                : "warning"
                    }
                />
            ),
        },
        {
            name: "Actions",
            cell: (row) => {
                const isCreator = row?.createdBy === loggedInUser?._id || row?.createdBy?._id === loggedInUser?._id;
                const isResponsible = row?.responsibleUser === loggedInUser?._id || row?.responsibleUser?._id === loggedInUser?._id;

                return (
                    <div className="flex items-center gap-2">
                        <Link to={`/app/crm/vehicle-hiring-entry/${row._id}/detail`}>
                            <IconButton size="small" aria-label="View" color="primary">
                                <InfoOutlined />
                            </IconButton>
                        </Link>
                        <Link to={`/app/crm/vehicle-hiring-entry/${row._id}/edit`}>
                            <IconButton size="small" aria-label="Edit">
                                <ModeEditOutlineTwoTone />
                            </IconButton>
                        </Link>

                        {/* WITHDRAW / RESUBMIT BUTTON - Only visible to creator */}
                        {isCreator && row?.isActive && activeTab !== "Rejected" && (
                            <Tooltip title="Withdraw Entry" arrow>
                                <IconButton
                                    size="small"
                                    color="warning"
                                    onClick={() => openWithdrawDialog(row)}
                                    disabled={isWithdrawing}
                                >
                                    <RemoveCircleOutline />
                                </IconButton>
                            </Tooltip>
                        )}
                        {isCreator && !row?.isActive && (
                            <Tooltip title="Resubmit Entry" arrow>
                                <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => setResubmitTarget(row)}
                                >
                                    <ReplayOutlined />
                                </IconButton>
                            </Tooltip>
                        )}

                        {/* APPROVE / REJECT - Only visible to the Approve / Submitted To user */}
                        {isResponsible && activeTab === "Pending" && (
                            <>
                                <IconButton
                                    size="small"
                                    color="success"
                                    onClick={() => handleApprove(row._id)}
                                    disabled={isReviewing}
                                >
                                    <CheckCircle />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => openRejectDialog(row)}
                                    disabled={isReviewing}
                                >
                                    <Cancel />
                                </IconButton>
                            </>
                        )}

                        {/* Resubmit button for Rejected tab - only creator */}
                        {isCreator && activeTab === "Rejected" && (
                            <IconButton
                                size="small"
                                color="primary"
                                onClick={() => setResubmitTarget(row)}
                            >
                                <ReplayOutlined />
                            </IconButton>
                        )}
                    </div>
                );
            },
            ignoreRowClick: true,
            right: true,
        },
    ];

    const [fetchEntries, { data: entryRes, isLoading, isFetching, error }] =
        useLazyListVehicleHiringEntriesQuery();

    React.useEffect(() => {
        const isInactiveTab = activeTab === "Inactive";
        fetchEntries({
            active: !isInactiveTab,
            approvalStatus: isInactiveTab ? "All" : activeTab,
            page: currentPage,
            limit: perPage,
            ...(keyword && { keyword }),
            ...(assignedToMe && { assignedToMe: true }),
            ...(startDate && { startDate }),
            ...(endDate && { endDate }),
        });
    }, [activeTab, currentPage, fetchEntries, keyword, perPage, assignedToMe, startDate, endDate]);

    const handleExcelExport = async () => {
        setIsExporting(true);
        try {
            const response = await fetchEntries({
                active: true,
                approvalStatus: "Approved",
                exportAll: "true",
                ...(startDate && { startDate }),
                ...(endDate && { endDate }),
            }).unwrap();
            exportVehicleHiringEntriesExcel({
                entries: response?.data || [],
                startDate,
                endDate,
            });
        } catch (err) {
            toast.error(err?.data?.message ?? "Failed to export entries");
        } finally {
            setIsExporting(false);
        }
    };

    useAppMeta(`Vehicle Hiring Entries - ${activeTab}`);

    return (
        <BreadcrumbLayout breadcrumbs={breadcrumbs} headerActions={<PageActions />}>
            <BreadcrumbLayout.Error error={error} />
            <BreadcrumbLayout.Paper>
                <CustomTabs tabs={tabs} activeTab={activeTab} />
                <Box sx={{ px: 2, pt: 2, display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                    <TextField
                        label="Created From"
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        size="small"
                        disabled={isLoading || isFetching}
                    />
                    <TextField
                        label="Created To"
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        size="small"
                        disabled={isLoading || isFetching}
                    />
                    <Button
                        variant="outlined"
                        onClick={() => {
                            setStartDate("");
                            setEndDate("");
                        }}
                        disabled={!startDate && !endDate}
                        size="small"
                    >
                        Clear Dates
                    </Button>
                </Box>
                <Box display="flex" alignItems="center" gap={2}>
                    <Tooltip title="Show only entries assigned to me" arrow>
                        <FormControlLabel
                            sx={{ ml: 1 }}
                            control={
                                <Switch
                                    checked={assignedToMe}
                                    onChange={() => setAssignedToMe((prev) => !prev)}
                                    color="primary"
                                    inputProps={{ "aria-label": "Assigned to Me Toggle" }}
                                />
                            }
                            label="Assigned to Me"
                        />
                    </Tooltip>
                    <Box flexGrow={1}>
                        <TableFilter
                            searchLabel="Search using Registration number"
                            disabled={isLoading || isFetching}
                        ></TableFilter>
                    </Box>
                    <Tooltip title="Exports Approved entries only" arrow>
                        <span>
                            <Button
                                variant="contained"
                                startIcon={<Download />}
                                onClick={handleExcelExport}
                                disabled={isExporting || isLoading || isFetching}
                            >
                                {isExporting ? "Exporting..." : "Export"}
                            </Button>
                        </span>
                    </Tooltip>
                </Box>
                <AppTable
                    columns={columns}
                    data={entryRes?.data ?? []}
                    progressPending={isLoading || isFetching}
                    paginationServer
                    paginationPerPage={perPage}
                    paginationTotalRows={entryRes?.totalItems ?? 0}
                    paginationDefaultPage={currentPage}
                    onChangePage={handlePageChange}
                    onChangeRowsPerPage={handlePerRowsChange}
                />
            </BreadcrumbLayout.Paper>
            <VehicleHiringEntryResubmitDialog
                open={!!resubmitTarget}
                entry={resubmitTarget}
                onClose={() => setResubmitTarget(null)}
            />
            <Dialog open={!!rejectTarget} onClose={() => setRejectTarget(null)} fullWidth maxWidth="sm">
                <DialogTitle>Rejection Note</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        multiline
                        rows={3}
                        margin="dense"
                        label="Reason for Rejection"
                        value={rejectNoteText}
                        onChange={(e) => {
                            setRejectNoteText(e.target.value);
                            if (rejectNoteError) setRejectNoteError("");
                        }}
                        error={!!rejectNoteError}
                        helperText={rejectNoteError || "Minimum 3 characters."}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRejectTarget(null)} disabled={isRejecting}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={handleSubmitReject} disabled={isRejecting}>
                        {isRejecting ? <CircularProgress size={20} /> : "Submit"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!withdrawTarget} onClose={() => setWithdrawTarget(null)} fullWidth maxWidth="sm">
                <DialogTitle>Withdraw Note</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        multiline
                        rows={3}
                        margin="dense"
                        label="Reason for Withdrawal"
                        value={withdrawNoteText}
                        onChange={(e) => {
                            setWithdrawNoteText(e.target.value);
                            if (withdrawNoteError) setWithdrawNoteError("");
                        }}
                        error={!!withdrawNoteError}
                        helperText={withdrawNoteError || "Minimum 3 characters."}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setWithdrawTarget(null)} disabled={isWithdrawingNote}>
                        Cancel
                    </Button>
                    <Button variant="contained" color="warning" onClick={handleSubmitWithdraw} disabled={isWithdrawingNote}>
                        {isWithdrawingNote ? <CircularProgress size={20} /> : "Withdraw"}
                    </Button>
                </DialogActions>
            </Dialog>
        </BreadcrumbLayout>
    );
};

export default VehicleHiringEntryListScreen;

const PageActions = () => {
    return (
        <div className="flex items-center gap-4">
            <Link to="/app/crm/vehicle-hiring-entry/create">
                <Button startIcon={<Add />} variant="contained">
                    New Hiring Entry
                </Button>
            </Link>
        </div>
    );
};
